import { graphql } from 'gatsby';
import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Container from '~/layout/Container';
import Image from '~/components/base/Image';
import BaseLink from '~/components/base/BaseLink';

import { Hiddenh2 } from '~/styles/helpers';
import { transition } from '~/styles/transition';

const NavWrapper = styled.div`
    background-color: ${({ theme }) => theme.colors.primary.white};
    position: relative;
    margin-top: 10px;

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        margin-top: 0;
    }
`;

const StyledImage = styled(Image)`
    position: absolute;
    display: none;
    bottom: 0;
    left: 0;
    width: 100%;

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        display: block;
        max-width: 18vw;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
        max-width: 308px;
    }
`;

const Nav = styled.nav`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 10px 0;
`;

const AppLinksWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 20px;
    margin-bottom: 20px;

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        display: none;
    }
`;

const AppImage = styled(Image)`
    max-width: 156px;
`;

const NavList = styled.ul`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px 25px;

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        flex-direction: row;
    }
`;

const NavLink = styled(BaseLink)`
    display: block;
    font-size: 1.5rem;
    color: ${({ theme }) => theme.colors.text.black};
    ${transition({}, 'text-decoration')};

    &:hover,
    &:focus {
        text-decoration: underline;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        font-size: 1.8rem;
    }
`;

const BottomFooterNavigation = ({ data }) => {
    const {
        allWpMenuItem: menuItems,
        wp: {
            siteSettings: {
                siteSettings: { cornerImage, appLinks }
            }
        }
    } = data;

    return menuItems?.nodes?.length ? (
        <NavWrapper>
            <Hiddenh2 id="secondaryFooterLabel">Secondary Footer</Hiddenh2>

            {cornerImage ? <StyledImage {...cornerImage} /> : null}

            <Container>
                {appLinks?.length ? (
                    <AppLinksWrapper>
                        {appLinks.map(({ linkUrl, badge }) => (
                            <BaseLink key={linkUrl} to={linkUrl} external>
                                <AppImage {...badge} />
                            </BaseLink>
                        ))}
                    </AppLinksWrapper>
                ) : null}
                <Nav>
                    <NavList aria-labelledby="secondaryFooterLabel">
                        {menuItems.nodes.map(({ id, url, label }) => (
                            <li key={id}>
                                <NavLink to={url}>{label}</NavLink>
                            </li>
                        ))}
                    </NavList>
                </Nav>
            </Container>
        </NavWrapper>
    ) : null;
};

BottomFooterNavigation.propTypes = {
    data: PropTypes.object.isRequired
};

export default BottomFooterNavigation;

export const query = graphql`
    query BottomFooter {
        wp {
            siteSettings {
                siteSettings {
                    cornerImage {
                        altText
                        sourceUrl
                        localFile {
                            childImageSharp {
                                gatsbyImageData(aspectRatio: 1.13)
                            }
                        }
                    }
                    appLinks {
                        linkUrl
                        badge {
                            altText
                            sourceUrl
                            localFile {
                                childImageSharp {
                                    gatsbyImageData(aspectRatio: 3)
                                }
                            }
                        }
                    }
                }
            }
        }
        allWpMenuItem(
            filter: {
                locations: { eq: FOOTER_MENU_BOTTOM }
                parentDatabaseId: { eq: 0 }
            }
            sort: { order: ASC }
        ) {
            nodes {
                url
                label
                id
            }
        }
    }
`;
